import { useId } from 'react'
import GoogleChart from '../GoogleChart'
import { generateComboChartData, getDefaultOptions } from './BurnChart.utils'
import { BurnChartProps } from './BurnChart.types'
import {
  Container,
  VerticalLegend,
  HorizontalLegend,
  Content,
  LineLegends
} from './BurnChart.styles'
import { Title } from 'site/Renderer/components/Cockpit/Cockpit.styles'
import { Text } from '@yes.technology/react-toolkit'
import { useTranslation } from 'react-i18n-lite'
import merge from 'lodash/merge'
import {
  BURN_CHART_IDEAL_COLOR,
  BURN_CHART_REAL_COLOR
} from './BurnChart.constants'

const BurnChart = ({
  title,
  data,
  direction = 'down',
  legend,
  showLegend,
  options: customOptions = {}
}: BurnChartProps) => {
  const chartUuid = useId()

  const options: BurnChartProps['options'] = merge(
    {},
    getDefaultOptions(data),
    customOptions
  )

  const { t } = useTranslation()

  const verticalLegendText =
    legend?.vertical || t('cockpit.burn-chart.vertical-legend')
  const startLegendText = legend?.start || t('cockpit.burn-chart.start-legend')
  const endLegendText = legend?.end || t('cockpit.burn-chart.end-legend')
  const idealLegendText = legend?.ideal || t('cockpit.burn-chart.ideal-legend')
  const realLegendText = legend?.real || t('cockpit.burn-chart.real-legend')

  const verticalLegendVisible = showLegend?.vertical ?? true
  const horizontalLegendVisible = showLegend?.horizontal ?? true
  const linesLegendVisible = showLegend?.lines ?? true

  return (
    <Container>
      <Title>{title}</Title>
      <Content>
        <VerticalLegend>
          {verticalLegendVisible ? verticalLegendText : ''}
        </VerticalLegend>
        <GoogleChart
          hideTitle
          title={title}
          chartModel='ComboChart'
          chartUuid={chartUuid}
          data={generateComboChartData(data, direction)}
          options={options}
        />
        {horizontalLegendVisible && (
          <HorizontalLegend>
            <Text variant='label-required-01'>{startLegendText}</Text>
            <Text variant='label-required-01'>{endLegendText}</Text>
          </HorizontalLegend>
        )}
        {linesLegendVisible && (
          <LineLegends>
            <Text variant='label-required-02'>
              <svg width={24} height={4}>
                <line
                  x1={0}
                  x2={16}
                  y1={0}
                  y2={0}
                  stroke={BURN_CHART_IDEAL_COLOR}
                  strokeWidth={4}
                />
              </svg>
              {idealLegendText}
            </Text>
            <Text variant='label-required-02'>
              <svg width={24} height={4}>
                <line
                  x1={0}
                  x2={16}
                  y1={0}
                  y2={0}
                  stroke={BURN_CHART_REAL_COLOR}
                  strokeWidth={4}
                />
              </svg>
              {realLegendText}
            </Text>
          </LineLegends>
        )}
      </Content>
    </Container>
  )
}

export default BurnChart
